import type { RollingSubmission } from "@/api/sourceData.service";
import { rollingStatusLabel } from "./sourceDataStatus";
import { sourceDataDate } from "./sourceDataDate";
import { DASH } from "./dash";

export type L500LiveItem = {
  id: string;
  name: string;
  meta: string[];
  description: string | null;
  marker: string | null;
  status: string | null;
};

export type L500LiveGroup = {
  heading: "Clients" | "Matters" | "Team members";
  items: L500LiveItem[];
};

export type L500LiveCard = {
  id: string;
  level: "Firm-level" | "Lawyer-level";
  date: string | null;
  status: string | null;
  groups: L500LiveGroup[];
  isEmpty: boolean;
};

function present(values: (string | null)[]): string[] {
  return values.filter((value): value is string => Boolean(value));
}

function group(heading: L500LiveGroup["heading"], items: L500LiveItem[]): L500LiveGroup[] {
  return items.length > 0 ? [{ heading, items }] : [];
}

function toCard(submission: RollingSubmission): L500LiveCard {
  const clients: L500LiveItem[] = submission.clients.map(client => ({
    id: client.id,
    name: client.clientCompanyName ?? DASH,
    meta: present([client.clientIndustry]),
    description: null,
    marker: null,
    status: rollingStatusLabel(client.status),
  }));

  const matters: L500LiveItem[] = submission.matters.map(matter => ({
    id: matter.id,
    name: matter.matterCompanyName ?? DASH,
    meta: present([sourceDataDate(matter.matterDate)]),
    description: matter.matterSummary,
    marker: null,
    status: rollingStatusLabel(matter.status),
  }));

  const active: L500LiveItem[] = submission.teamActive.map(member => ({
    id: member.id,
    name: member.activeName ?? DASH,
    meta: present([member.activeJobTitle ?? member.activeRole, member.activeLocation]),
    description: null,
    marker: null,
    status: rollingStatusLabel(member.status),
  }));

  const departed: L500LiveItem[] = submission.teamDeparted.map(member => ({
    id: member.id,
    name: member.departedName ?? DASH,
    meta: present([sourceDataDate(member.departedDate)]),
    description: null,
    marker: "Departed",
    status: rollingStatusLabel(member.status),
  }));

  const groups = [
    ...group("Clients", clients),
    ...group("Matters", matters),
    ...group("Team members", [...active, ...departed]),
  ];

  return {
    id: submission.id,
    level: submission.contactUuid ? "Lawyer-level" : "Firm-level",
    date: sourceDataDate(submission.publishedAt ?? submission.createdAt),
    status: rollingStatusLabel(submission.approvalStatus),
    groups,
    isEmpty: groups.length === 0,
  };
}

export function l500LiveCards(submissions: RollingSubmission[]): L500LiveCard[] {
  return submissions.map(toCard);
}
